import { useMemo } from 'react';
import { IconButton } from 'src/components/Buttons';
import * as styles from 'src/components/Buttons/IconButton/IconButton.css';
import { MarketplaceLogo } from 'src/components/common/MarketplaceLogo';
import { useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { AnimatedBroadcastIcon, AnimatedUnbroadcastIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKET_PLACE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  getListingBroadcastIcon,
  handleAnyTicketsViagogoInternationalListingStatus,
} from 'src/utils/inventoryUtils';
import {
  Event,
  Listing,
  ListingStatus,
  Marketplace,
  PendingProofState,
} from 'src/WebApiController';

import {
  getListingBroadcastEnabled,
  MarketplaceDisplayProps,
} from '../MarketplaceBroadcastSection.utils';

export const MarketplaceBroadcastIcon = ({
  marketplaceListing: ml,
  status,
  setListingStatus,
  isUKEvent,
  isViagogo,
  isDeleted,
  disabled,
  onListingBroadcastToggle,
  error,
  qcState,
  event,
  listing,
}: MarketplaceDisplayProps & {
  event: Event;
  listing: Listing;
}) => {
  const {
    broadcastChecked,
    broadcastDisabled,
    alwaysShowAsDelisted,
    hasBroadcastErrors,
  } = getListingBroadcastEnabled(
    listing,
    ml,
    status,
    qcState,
    isUKEvent,
    isViagogo,
    disabled,
    isDeleted
  );

  const { contentId } = getListingBroadcastIcon(
    alwaysShowAsDelisted
      ? ListingStatus.Delisted // We always show Delisted for StubHub when event is in UK
      : status,
    qcState,
    false,
    true,
    listing?.isUndeliv,
    ml.brdcstStatus,
    ml.pendProofState
  );

  const hasError = useMemo(() => {
    if (error != null) {
      return true;
    }

    if (hasBroadcastErrors) {
      return true;
    }

    if (
      ml.pendProofState === PendingProofState.PendingProof ||
      ml.pendProofState === PendingProofState.PendingReview
    ) {
      return true;
    }

    return false;
  }, [error, ml.pendProofState, hasBroadcastErrors]);

  return (
    <MarketplaceIconButton
      marketplace={ml.mkp}
      event={event!}
      isViagogo={isViagogo}
      hasError={hasError}
      checked={broadcastChecked}
      disabled={broadcastDisabled}
      onChange={() => {
        if (ml && !isDeleted) {
          const newStatus = broadcastChecked
            ? ListingStatus.DelistingPending
            : ListingStatus.ListingPending;
          setListingStatus(ml.mkp!, newStatus);

          // the following code is for AnyTickets ViagogoInternational status
          // see https://thestubhub.atlassian.net/wiki/spaces/ENG/pages/2676785153 for details.
          handleAnyTicketsViagogoInternationalListingStatus(
            listing,
            ml.mkp!,
            newStatus,
            broadcastChecked,
            setListingStatus
          );

          onListingBroadcastToggle(!broadcastChecked, ml.mkp!);
        }
      }}
      status={status}
      titleContentId={contentId}
    />
  );
};

const MarketplaceIconButton = ({
  marketplace,
  hasError,
  status,
  disabled,
  onChange,
  checked,
  event,
  isViagogo,
  titleContentId,
}: {
  marketplace: Marketplace;
  hasError: boolean;
  disabled?: boolean;
  status: ListingStatus;
  onChange: () => void;
  checked: boolean;
  event: Event;
  isViagogo?: boolean;
  titleContentId?: string | ContentId;
}) => {
  const title = useContent(titleContentId || ContentId.EmptyContent);
  const marketplaceName = useContent(MARKET_PLACE_TO_CID[marketplace]);

  return (
    <IconButton
      style={{ padding: 0 }}
      icon={
        <>
          {status === ListingStatus.ListingPending ? (
            <AnimatedBroadcastIcon size={vars.iconSize.l} />
          ) : status === ListingStatus.DelistingPending ? (
            <AnimatedUnbroadcastIcon size={vars.iconSize.l} />
          ) : (
            <MarketplaceLogo
              marketplace={marketplace}
              event={event}
              isViagogo={isViagogo}
              variant="square"
              logoDimensions={{ width: 24, height: 24 }}
              onClick={() => void 0}
              grayScaleColor={disabled}
              disabled={disabled}
              showErrorDotOverlay={hasError}
            />
          )}
        </>
      }
      disabled={disabled}
      disableCursor={disabled}
      onClick={onChange}
      className={
        !checked && !disabled
          ? styles.iconButtonContainerVariants.inactive
          : undefined
      }
      titleContentId={title ? `${marketplaceName}: ${title}` : marketplaceName}
    />
  );
};
